// Mock Firebase Authentication Service for Development

interface MockUser {
  uid: string;
  phoneNumber: string;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
  [key: string]: any;
}

interface MockConfirmationResult {
  confirm: (code: string) => Promise<{ user: MockUser }>;
}

interface MockUserProfile {
  uid: string;
  phoneNumber: string;
  fullName?: string;
  email?: string;
  profileComplete: boolean;
}

class MockAuthService {
  private _currentUser: MockUser | null = null;
  private authStateListeners: ((user: MockUser | null) => void)[] = [];
  
  // Expose currentUser as a getter to match Firebase auth interface
  get currentUser(): MockUser | null {
    return this._currentUser;
  }

  constructor() {
    // Restore user from localStorage on initialization
    this.restoreUserFromStorage();
    // Notify listeners after restoration
    setTimeout(() => this.notifyAuthStateListeners(), 0);
  }

  // Mock test credentials
  private testCredentials = {
    phoneNumbers: ['+972501234567', '+972523456789'],
    otpCode: '123456'
  };

  // Simulate onAuthStateChanged
  onAuthStateChanged(callback: (user: MockUser | null) => void) {
    this.authStateListeners.push(callback);
    // Immediately call with current user
    callback(this._currentUser);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  // Simulate signInWithPhoneNumber
  async signInWithPhoneNumber(phoneNumber: string): Promise<MockConfirmationResult> {
    return {
      confirm: async (code: string) => {
        // Simulate OTP verification delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (code === this.testCredentials.otpCode) {
          const mockUser: MockUser = {
            uid: `mock-user-${Date.now()}`,
            phoneNumber: phoneNumber,
            getIdToken: async (forceRefresh?: boolean) => {
              console.log('🔧 Mock: Getting ID token (forceRefresh:', forceRefresh, ')');
              // Generate a mock JWT-like token for development
              const mockToken = `mock-firebase-token-${mockUser.uid}-${Date.now()}`;
              console.log('🔧 Mock: Generated token:', mockToken.substring(0, 30) + '...');
              return mockToken;
            }
          };
          
          this._currentUser = mockUser;
          this.saveUserToStorage();
          this.notifyAuthStateListeners();
          
          return { user: mockUser };
        } else {
          throw new Error('Invalid verification code');
        }
      }
    };
  }

  // Simulate signOut
  async signOut(): Promise<void> {
    this._currentUser = null;
    localStorage.removeItem('mockUser');
    localStorage.removeItem('mockUserProfile');
    this.notifyAuthStateListeners();
  }

  // Get current user
  getCurrentUser(): MockUser | null {
    return this._currentUser;
  }

  private saveUserToStorage() {
    if (this._currentUser) {
      // Only save serializable data, not the function
      const userDataToSave = {
        uid: this._currentUser.uid,
        phoneNumber: this._currentUser.phoneNumber
      };
      localStorage.setItem('mockUser', JSON.stringify(userDataToSave));
    }
  }

  private restoreUserFromStorage() {
    const stored = localStorage.getItem('mockUser');
    if (stored) {
      try {
        const userData = JSON.parse(stored);
        // Restore user with the getIdToken method
        this._currentUser = {
          ...userData,
          getIdToken: async (forceRefresh?: boolean) => {
            console.log('🔧 Mock: Getting ID token (forceRefresh:', forceRefresh, ')');
            // Generate a mock JWT-like token for development
            const mockToken = `mock-firebase-token-${userData.uid}-${Date.now()}`;
            console.log('🔧 Mock: Generated token:', mockToken.substring(0, 30) + '...');
            return mockToken;
          }
        };
        console.log('🔧 Mock: User restored from storage:', userData.uid);
      } catch (error) {
        console.error('Error restoring mock user:', error);
        localStorage.removeItem('mockUser');
      }
    }
  }

  private notifyAuthStateListeners() {
    this.authStateListeners.forEach(callback => callback(this._currentUser));
  }
}

class MockFirestore {
  // Simulate Firestore document operations
  async getDoc(docRef: { path: string }): Promise<{ exists: () => boolean; data: () => any }> {
    const stored = localStorage.getItem(`firestore-${docRef.path}`);
    
    return {
      exists: () => !!stored,
      data: () => stored ? JSON.parse(stored) : null
    };
  }

  async setDoc(docRef: { path: string }, data: any): Promise<void> {
    localStorage.setItem(`firestore-${docRef.path}`, JSON.stringify(data));
  }

  // Create document reference
  doc(collection: string, id: string) {
    return { path: `${collection}/${id}` };
  }
}

// Export mock services
export const mockAuth = new MockAuthService();
export const mockFirestore = new MockFirestore();

// Mock test phone numbers for development
export const TEST_PHONE_NUMBERS = [
  '+972501234567',
  '+972523456789', 
  '+972541234567'
];

export const TEST_OTP_CODE = '123456'; 